import React, { useState, useEffect, useRef } from 'react';
import {
  Box, VStack, Heading, Text, Select, Input, Button, FormControl, FormLabel,
  SimpleGrid, Alert, AlertIcon, Badge, HStack, Spinner, Flex, Card, CardBody,
  List, ListItem, ListIcon, useToast, Textarea, RadioGroup, Radio, Stack, Divider,
} from '@chakra-ui/react';
import { CheckCircleIcon, AttachmentIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { obtenerTiposPublicoOP, obtenerRequisitosTipoOP, tramiteDigitalOP, subirDocumentoDigitalOP } from '../../api/opTramiteApi';

const PASOS = ['1. Trámite', '2. Solicitante', '3. Domicilio', '4. Documentos', '5. Confirmación'];

const StepperSimple: React.FC<{ pasos: string[]; activo: number }> = ({ pasos, activo }) => (
  <HStack w="full" spacing={1} flexWrap="wrap" justify="center">
    {pasos.map((p, i) => (
      <Badge key={i} colorScheme={i < activo ? 'green' : i === activo ? 'red' : 'gray'}
        px={3} py={1} borderRadius="full" fontSize="xs" opacity={i > activo ? 0.5 : 1}>
        {i < activo ? '✓ ' : ''}{p}
      </Badge>
    ))}
  </HStack>
);

const PortalTramiteDigitalOP: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [tipos, setTipos] = useState<any[]>([]);
  const [requisitos, setRequisitos] = useState<any[]>([]);
  const [loadingTipos, setLoadingTipos] = useState(true);
  const [loadingReq, setLoadingReq] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState<{ folio: string; idTramite: number } | null>(null);
  const [archivos, setArchivos] = useState<Array<{ file: File; descripcion: string }>>([]);
  const [subiendoDocs, setSubiendoDocs] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const [form, setForm] = useState({
    idTipoTramite: '', tipoPersona: 'FISICA',
    nombreSolicitante: '', primerApellido: '', segundoApellido: '',
    razonSocial: '', representanteLegal: '', rfc: '', curp: '',
    telefono: '', celular: '', correo: '',
    calleSolicitante: '', numeroExteriorSol: '', numeroInteriorSol: '',
    coloniaSolicitante: '', municipioDomicilio: '', estadoDomicilio: 'Tlaxcala',
    codigoPostal: '', nomenclatura: '', direccion: '', observaciones: '',
  });

  const set = (f: string, v: string) => setForm(prev => ({ ...prev, [f]: v }));

  useEffect(() => {
    obtenerTiposPublicoOP()
      .then(setTipos)
      .catch(() => {})
      .finally(() => setLoadingTipos(false));
  }, []);

  useEffect(() => {
    if (!form.idTipoTramite) { setRequisitos([]); return; }
    setLoadingReq(true);
    obtenerRequisitosTipoOP(Number(form.idTipoTramite))
      .then(d => setRequisitos(d.requisitos || []))
      .catch(() => setRequisitos([]))
      .finally(() => setLoadingReq(false));
  }, [form.idTipoTramite]);

  const validarPaso = () => {
    if (activeStep === 0 && !form.idTipoTramite) {
      toast({ title: 'Seleccione el tipo de trámite', status: 'warning', duration: 2000 }); return false;
    }
    if (activeStep === 1) {
      if (!form.telefono) { toast({ title: 'El teléfono es obligatorio', status: 'warning', duration: 2000 }); return false; }
      if (!form.correo) { toast({ title: 'El correo es obligatorio', status: 'warning', duration: 2000 }); return false; }
      if (form.tipoPersona === 'FISICA' && !form.nombreSolicitante) {
        toast({ title: 'El nombre es obligatorio', status: 'warning', duration: 2000 }); return false;
      }
      if (form.tipoPersona === 'MORAL' && !form.razonSocial) {
        toast({ title: 'La razón social es obligatoria', status: 'warning', duration: 2000 }); return false;
      }
    }
    return true;
  };

  const siguiente = () => { if (validarPaso()) setActiveStep(s => s + 1); };
  const anterior = () => setActiveStep(s => s - 1);

  const handleRegistrar = async () => {
    setLoading(true);
    try {
      const res = await tramiteDigitalOP(form);
      setResultado(res);

      // Subir documentos si los hay
      if (archivos.length > 0) {
        setSubiendoDocs(true);
        for (const a of archivos) {
          try { await subirDocumentoDigitalOP(res.idTramite, a.file, a.descripcion); } catch {}
        }
        setSubiendoDocs(false);
      }

      setActiveStep(4);
    } catch (err: any) {
      toast({ title: 'Error al registrar', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setLoading(false);
    }
  };

  const addArchivo = (file: File) => setArchivos(prev => [...prev, { file, descripcion: '' }]);
  const removeArchivo = (i: number) => setArchivos(prev => prev.filter((_, idx) => idx !== i));
  const setDescripcionArchivo = (i: number, desc: string) =>
    setArchivos(prev => prev.map((a, idx) => idx === i ? { ...a, descripcion: desc } : a));

  return (
    <Box minH="100vh" bg="gray.50" py={8} px={{ base: 4, md: 10 }}>
      <VStack spacing={6} maxW="850px" mx="auto">
        <Box w="full">
          <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" color="gray.500" onClick={() => navigate('/portal/obras')}>
            Portal Obras Públicas
          </Button>
        </Box>

        <Box textAlign="center">
          <Box bg="#5b182b" borderRadius="full" w={14} h={14} mx="auto" mb={3} display="flex" alignItems="center" justifyContent="center">
            <AttachmentIcon color="white" boxSize={6} />
          </Box>
          <Heading size="lg" color="#5b182b">Trámite Digital</Heading>
          <Text color="gray.500" mt={1}>H. Ayuntamiento de Totolac · Dirección de Obras Públicas</Text>
        </Box>

        <StepperSimple pasos={PASOS} activo={activeStep} />

        {/* Paso 0: Tipo de trámite */}
        {activeStep === 0 && (
          <Card w="full" shadow="sm">
            <CardBody>
              <Heading size="sm" mb={4} color="#5b182b">Seleccione el Trámite</Heading>
              <FormControl isRequired mb={4}>
                <FormLabel fontSize="sm">Tipo de trámite</FormLabel>
                {loadingTipos ? <Spinner size="sm" /> : (
                  <Select value={form.idTipoTramite} onChange={e => set('idTipoTramite', e.target.value)} placeholder="— Seleccione —">
                    {tipos.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                  </Select>
                )}
              </FormControl>

              {loadingReq && <Spinner size="sm" />}
              {requisitos.length > 0 && (
                <Box>
                  <Text fontWeight="bold" fontSize="sm" mb={2}>Documentos requeridos:</Text>
                  <List spacing={1}>
                    {requisitos.map((r: any) => (
                      <ListItem key={r.id} fontSize="sm">
                        <ListIcon as={CheckCircleIcon} color={r.obligatorio ? 'red.400' : 'gray.400'} />
                        {r.nombreRequisito}
                        {r.obligatorio && <Badge colorScheme="red" ml={1} fontSize="xs">Obligatorio</Badge>}
                      </ListItem>
                    ))}
                  </List>
                  <Text fontSize="xs" color="gray.500" mt={2}>Podrá cargar los documentos en el paso 4. No todos son obligatorios al inicio.</Text>
                </Box>
              )}
            </CardBody>
          </Card>
        )}

        {/* Paso 1: Solicitante */}
        {activeStep === 1 && (
          <Card w="full" shadow="sm">
            <CardBody>
              <Heading size="sm" mb={4} color="#5b182b">Datos del Solicitante</Heading>
              <FormControl mb={4}>
                <FormLabel fontSize="sm">Tipo de persona</FormLabel>
                <RadioGroup value={form.tipoPersona} onChange={v => set('tipoPersona', v)}>
                  <Stack direction="row"><Radio value="FISICA">Física</Radio><Radio value="MORAL">Moral</Radio></Stack>
                </RadioGroup>
              </FormControl>
              {form.tipoPersona === 'FISICA' ? (
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                  <FormControl isRequired><FormLabel fontSize="sm">Nombre(s)</FormLabel><Input size="sm" value={form.nombreSolicitante} onChange={e => set('nombreSolicitante', e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="sm">Primer Apellido</FormLabel><Input size="sm" value={form.primerApellido} onChange={e => set('primerApellido', e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="sm">Segundo Apellido</FormLabel><Input size="sm" value={form.segundoApellido} onChange={e => set('segundoApellido', e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="sm">RFC</FormLabel><Input size="sm" value={form.rfc} onChange={e => set('rfc', e.target.value.toUpperCase())} maxLength={13} /></FormControl>
                  <FormControl><FormLabel fontSize="sm">CURP</FormLabel><Input size="sm" value={form.curp} onChange={e => set('curp', e.target.value.toUpperCase())} maxLength={18} /></FormControl>
                </SimpleGrid>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl isRequired><FormLabel fontSize="sm">Razón Social</FormLabel><Input size="sm" value={form.razonSocial} onChange={e => set('razonSocial', e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="sm">Representante Legal</FormLabel><Input size="sm" value={form.representanteLegal} onChange={e => set('representanteLegal', e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="sm">RFC</FormLabel><Input size="sm" value={form.rfc} onChange={e => set('rfc', e.target.value.toUpperCase())} maxLength={13} /></FormControl>
                </SimpleGrid>
              )}
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mt={3}>
                <FormControl isRequired><FormLabel fontSize="sm">Teléfono</FormLabel><Input size="sm" type="tel" value={form.telefono} onChange={e => set('telefono', e.target.value)} /></FormControl>
                <FormControl><FormLabel fontSize="sm">Celular</FormLabel><Input size="sm" type="tel" value={form.celular} onChange={e => set('celular', e.target.value)} /></FormControl>
                <FormControl isRequired><FormLabel fontSize="sm">Correo electrónico</FormLabel><Input size="sm" type="email" value={form.correo} onChange={e => set('correo', e.target.value)} /></FormControl>
              </SimpleGrid>
            </CardBody>
          </Card>
        )}

        {/* Paso 2: Domicilio */}
        {activeStep === 2 && (
          <Card w="full" shadow="sm">
            <CardBody>
              <Heading size="sm" mb={4} color="#5b182b">Domicilio del Predio / Solicitante</Heading>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                <FormControl><FormLabel fontSize="sm">Calle</FormLabel><Input size="sm" value={form.calleSolicitante} onChange={e => set('calleSolicitante', e.target.value)} /></FormControl>
                <FormControl><FormLabel fontSize="sm">No. Exterior</FormLabel><Input size="sm" value={form.numeroExteriorSol} onChange={e => set('numeroExteriorSol', e.target.value)} /></FormControl>
                <FormControl><FormLabel fontSize="sm">No. Interior</FormLabel><Input size="sm" value={form.numeroInteriorSol} onChange={e => set('numeroInteriorSol', e.target.value)} /></FormControl>
                <FormControl><FormLabel fontSize="sm">Colonia</FormLabel><Input size="sm" value={form.coloniaSolicitante} onChange={e => set('coloniaSolicitante', e.target.value)} /></FormControl>
                <FormControl><FormLabel fontSize="sm">Municipio</FormLabel><Input size="sm" value={form.municipioDomicilio} onChange={e => set('municipioDomicilio', e.target.value)} /></FormControl>
                <FormControl><FormLabel fontSize="sm">Estado</FormLabel><Input size="sm" value={form.estadoDomicilio} onChange={e => set('estadoDomicilio', e.target.value)} /></FormControl>
                <FormControl><FormLabel fontSize="sm">Código Postal</FormLabel><Input size="sm" value={form.codigoPostal} onChange={e => set('codigoPostal', e.target.value)} maxLength={5} /></FormControl>
              </SimpleGrid>
              <FormControl mt={3}>
                <FormLabel fontSize="sm">Descripción del predio / ubicación de la obra</FormLabel>
                <Textarea size="sm" rows={3} value={form.direccion} onChange={e => set('direccion', e.target.value)} placeholder="Ej. Predio ubicado en la Calle Hidalgo s/n, entre las calles..." />
              </FormControl>
              <FormControl mt={3}>
                <FormLabel fontSize="sm">Observaciones (opcional)</FormLabel>
                <Textarea size="sm" rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} />
              </FormControl>
            </CardBody>
          </Card>
        )}

        {/* Paso 3: Documentos */}
        {activeStep === 3 && (
          <Card w="full" shadow="sm">
            <CardBody>
              <Heading size="sm" mb={2} color="#5b182b">Carga de Documentos</Heading>
              <Text fontSize="sm" color="gray.500" mb={4}>Puede cargar documentos ahora o presentarlos en las oficinas. PDF, JPG, PNG, DOC — máx. 10 MB por archivo.</Text>
              {requisitos.length > 0 && (
                <Box mb={4} p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="xs" fontWeight="bold" mb={1}>Documentos requeridos para este trámite:</Text>
                  {requisitos.map((r: any) => (
                    <Text key={r.id} fontSize="xs">• {r.nombreRequisito}{r.obligatorio ? ' (obligatorio)' : ''}</Text>
                  ))}
                </Box>
              )}
              <Button size="sm" leftIcon={<AttachmentIcon />} colorScheme="blue" variant="outline" mb={3}
                onClick={() => fileRef.current?.click()}>
                Agregar archivo
              </Button>
              <input ref={fileRef} type="file" style={{ display: 'none' }} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={e => { if (e.target.files?.[0]) { addArchivo(e.target.files[0]); e.target.value = ''; } }} />
              {archivos.length === 0 && (
                <Text fontSize="sm" color="gray.400" textAlign="center" py={4}>No se han agregado documentos (puede continuar sin archivos)</Text>
              )}
              <VStack align="stretch" spacing={2}>
                {archivos.map((a, i) => (
                  <HStack key={i} p={2} bg="gray.50" borderRadius="md" justify="space-between">
                    <Box flex={1}>
                      <Text fontSize="sm">{a.file.name}</Text>
                      <Input size="xs" value={a.descripcion} onChange={e => setDescripcionArchivo(i, e.target.value)}
                        placeholder="Descripción (ej. INE frontal)" mt={1} />
                    </Box>
                    <Button size="xs" colorScheme="red" variant="ghost" onClick={() => removeArchivo(i)}>✕</Button>
                  </HStack>
                ))}
              </VStack>
            </CardBody>
          </Card>
        )}

        {/* Paso 4: Confirmación */}
        {activeStep === 4 && resultado && (
          <Card w="full" shadow="md">
            <CardBody textAlign="center">
              <CheckCircleIcon color="green.500" boxSize={16} mb={3} />
              <Heading size="lg" color="green.600">¡Trámite registrado!</Heading>
              <Text color="gray.600" mt={2}>Su folio de seguimiento es:</Text>
              <Text fontFamily="mono" fontSize="2xl" fontWeight="bold" color="#5b182b" my={2}>{resultado.folio}</Text>
              {subiendoDocs && <Flex justify="center" align="center" gap={2}><Spinner size="sm" /><Text fontSize="sm">Subiendo documentos...</Text></Flex>}
              <Divider my={3} />
              <Text fontSize="sm" color="gray.500">
                Su trámite ha sido registrado con estatus <Badge colorScheme="blue">REGISTRADO</Badge>.<br />
                El personal de Obras Públicas revisará su solicitud y documentación.<br />
                Puede consultar el estatus con su folio en el portal ciudadano.
              </Text>
            </CardBody>
          </Card>
        )}

        {/* Botones de navegación */}
        {activeStep < 4 && (
          <HStack w="full" justify="space-between">
            {activeStep > 0 ? (
              <Button variant="outline" onClick={anterior}>← Anterior</Button>
            ) : <Box />}
            {activeStep < 3 ? (
              <Button colorScheme="red" onClick={siguiente}>Siguiente →</Button>
            ) : (
              <Button colorScheme="red" onClick={handleRegistrar} isLoading={loading}>
                Enviar Trámite
              </Button>
            )}
          </HStack>
        )}

        <Text fontSize="xs" color="gray.400" textAlign="center">
          Dirección de Obras Públicas · H. Ayuntamiento de Totolac · Tlaxcala
        </Text>
      </VStack>
    </Box>
  );
};

export default PortalTramiteDigitalOP;
